import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { ProgramWizardBatteryConfig, ProgramWizardData, ProgramWizardOperatingMode } from '../../ProgramWizard.types';

type ConfirmStepContentProps = {
  wizardData: ProgramWizardData;
  configData: ProgramWizardBatteryConfig;
};

const ConfirmStepContent = ({ wizardData, configData }: ConfirmStepContentProps) => {
  const { t } = useTranslation('programWizard');
  const basicBulletPoints = t('confirmOperatingModeText.basicBulletPoints', { returnObjects: true }) as string[];
  const standbyBulletPoints = t('confirmOperatingModeText.standbyBulletPoints', {
    returnObjects: true,
  }) as string[];
  const shutdownBulletPoints = t('confirmOperatingModeText.shutdownBulletPoints', {
    returnObjects: true,
  }) as string[];

  const batteryDetails = [
    t('confirmOperatingModeText.batteryDetailSoc', {
      lower: configData.target_soc_lower_vs_gen_set,
      hysteresis: configData.target_soc_hysteresis_vs_gen_set,
    }),
    t('confirmOperatingModeText.batteryDetailLoad', { power: configData.nominal_battery_power }),
  ];

  const operatingModeText: Partial<
    Record<
      ProgramWizardOperatingMode,
      { topText: string; topBullets?: string[]; bulletPoints: string[]; batteryDetails?: boolean; bottomText?: string }
    >
  > = {
    [ProgramWizardOperatingMode.STANDALONE]: {
      topText: t('confirmOperatingModeText.areYouSure', { mode: t('confirmOperatingModeText.modes.standalone') }),
      bulletPoints: basicBulletPoints,
    },
    [ProgramWizardOperatingMode.GRID]: {
      topText: t('confirmOperatingModeText.areYouSure', { mode: t('confirmOperatingModeText.modes.grid') }),
      bulletPoints: [
        ...basicBulletPoints,
        t('confirmOperatingModeText.gridSocDetail', { soc: configData.soc_setpoint }),
      ],
    },
    [ProgramWizardOperatingMode.GENSETS]: {
      topText: t('confirmOperatingModeText.areYouSure', { mode: t('confirmOperatingModeText.modes.gensets') }),
      bulletPoints: [...basicBulletPoints, t('confirmOperatingModeText.gensetChargeIntro')],
      batteryDetails: true,
    },
    [ProgramWizardOperatingMode.GEN_SET_CYCLE]: {
      topText: t('confirmOperatingModeText.areYouSure', { mode: t('confirmOperatingModeText.modes.gensetCycle') }),
      bulletPoints: [...basicBulletPoints, t('confirmOperatingModeText.gensetChargeIntro')],
      batteryDetails: true,
    },
    [ProgramWizardOperatingMode.LOADSHARE]: {
      topText: t('confirmOperatingModeText.areYouSure', { mode: t('confirmOperatingModeText.modes.loadshare') }),
      bulletPoints: basicBulletPoints,
    },
    [ProgramWizardOperatingMode.STANDBY]: {
      topText: t('confirmOperatingModeText.areYouSure', { mode: t('confirmOperatingModeText.modes.standby') }),
      bulletPoints: standbyBulletPoints,
    },
    [ProgramWizardOperatingMode.SHUTDOWN]: {
      topText: t('confirmOperatingModeText.shutdownBeforeConfirm'),
      topBullets: [
        t('confirmOperatingModeText.shutdownCheckVoltage'),
        t('confirmOperatingModeText.shutdownCheckBreaker'),
      ],
      bulletPoints: shutdownBulletPoints,
      bottomText: t('confirmOperatingModeText.shutdownProceedQuestion'),
    },
  };

  const modeText = wizardData.operatingMode !== null ? operatingModeText[wizardData.operatingMode] : undefined;

  const hasConfirmBullets =
    Boolean(wizardData.maxInputCurrent) ||
    wizardData.operatingMode === ProgramWizardOperatingMode.GENSETS ||
    wizardData.operatingMode === ProgramWizardOperatingMode.GEN_SET_CYCLE ||
    Boolean(wizardData.loadShareMasterType);

  return (
    <Box>
      <Typography variant="body1" mb={1}>
        {modeText?.topText}
      </Typography>

      {modeText?.topBullets && (
        <ul>
          {modeText.topBullets.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
      )}

      {hasConfirmBullets && (
        <ul>
          {wizardData.maxInputCurrent && (
            <li key={0}>{t('confirmOperatingModeText.maxInputCurrentValue', { value: wizardData.maxInputCurrent })}</li>
          )}
          {(wizardData.operatingMode === ProgramWizardOperatingMode.GENSETS ||
            wizardData.operatingMode === ProgramWizardOperatingMode.GEN_SET_CYCLE) &&
            Object.values(wizardData.genSetData).map((gen, i) => {
              return (
                gen && <li key={i}>{t('confirmOperatingModeText.ratedPowerGenset', { index: i + 1, value: gen })}</li>
              );
            })}
          {wizardData.loadShareMasterType && (
            <li key={5}>
              {t('confirmOperatingModeText.loadshareMasterTypeValue', {
                value: wizardData.loadShareMasterType,
              })}
            </li>
          )}
        </ul>
      )}

      <Typography>{t('confirmOperatingModeText.afterConfirm')}</Typography>

      <ul>
        {modeText?.bulletPoints && modeText?.bulletPoints.map((point, i) => <li key={i}>{point}</li>)}
        {modeText?.batteryDetails && (
          <ul>
            {batteryDetails.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        )}
      </ul>

      {modeText?.bottomText && (
        <Typography variant="body1" mt={1}>
          {modeText.bottomText}
        </Typography>
      )}
    </Box>
  );
};

export default ConfirmStepContent;
